import { useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import SegmentedControl from "@plannel/components/ui/SegmentedControl";
import dateUtils, { DEFAULT_DATE_FORMAT } from "@plannel/utils/date-util";
import { FS } from "@plannel/utils/font-util";

/**
 * 기간 필터. 필터 툴바에서 자리를 거의 차지하지 않도록 버튼 하나로 접어 두고,
 * 누르면 팝오버에서 축과 범위를 고른다.
 *
 * <p><b>범위가 걸려 있으면 버튼이 값을 보여 준다.</b> 아이콘만 두면 필터가 켜진 줄 모르고
 * "왜 건수가 적지"로 이어진다 — 접힌 필터가 조용히 결과를 줄이는 것이 가장 흔한 혼란이다.
 */
const AgendaPeriodFilter = ({ conditionList, selectedSearch, setSelectedSearch, period, setPeriod, t }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const hasPeriod = Boolean(period.start || period.end);
  const axisLabel = t(conditionList.find((c) => c.cd === selectedSearch)?.name ?? "");

  const rangeLabel = () => {
    const from = period.start ? dateUtils.formatDate(period.start, DEFAULT_DATE_FORMAT) : "";
    const to = period.end ? dateUtils.formatDate(period.end, DEFAULT_DATE_FORMAT) : "";
    if (from && to) {
      return `${from} ~ ${to}`;
    }
    // 한쪽만 고른 경우도 의미가 있다 — "9/20 이후" 는 흔한 조회다
    return from ? `${from} ~` : `~ ${to}`;
  };

  const clear = (event) => {
    event.stopPropagation();
    setPeriod({ start: null, end: null });
  };

  return (
    <>
      <Button
        size="small"
        variant={hasPeriod ? "outlined" : "text"}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        startIcon={<DateRangeOutlinedIcon />}
        endIcon={
          hasPeriod ? (
            <Box
              component="span"
              role="button"
              tabIndex={0}
              aria-label={t("MSG_MeetingAgendaClearPeriod")}
              onClick={clear}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  clear(event);
                }
              }}
              sx={{ display: "inline-flex", color: "text.disabled", "&:hover": { color: "text.primary" } }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </Box>
          ) : null
        }
        sx={{
          height: 36,
          color: hasPeriod ? "text.primary" : "text.secondary",
          fontSize: FS.BODY,
          fontWeight: hasPeriod ? 600 : 400,
          whiteSpace: "nowrap"
        }}
      >
        {hasPeriod ? (
          <Box component="span">
            <Box component="span" sx={{ color: "text.secondary", fontWeight: 400, mr: 0.5 }}>
              {axisLabel}
            </Box>
            {rangeLabel()}
          </Box>
        ) : (
          t("period")
        )}
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5, width: 320 }}>
          <Box>
            <Typography sx={{ fontSize: FS.CAPTION, fontWeight: 700, color: "text.secondary", mb: 0.75 }}>
              {t("MSG_MeetingAgendaDateAxis")}
            </Typography>
            <SegmentedControl
              value={selectedSearch}
              onChange={setSelectedSearch}
              options={conditionList.map((c) => ({ value: c.cd, label: t(c.name) }))}
            />
          </Box>

          {/* DatePicker 는 LocalizationProvider 안에서만 동작한다. 이 앱은 전역에 두지 않고
              화면마다 감싼다 — 빼면 피커를 여는 순간 터진다 */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DatePicker
              label={t("periodStart")}
              views={["year", "month", "day"]}
              format={DEFAULT_DATE_FORMAT}
              value={period.start}
              onChange={(value) => setPeriod({ ...period, start: value })}
              slotProps={{ textField: { size: "small", sx: { width: 140 } } }}
            />
              <Box sx={{ color: "text.disabled" }}>~</Box>
              <DatePicker
              label={t("periodEnd")}
              views={["year", "month", "day"]}
              format={DEFAULT_DATE_FORMAT}
              value={period.end}
              onChange={(value) => setPeriod({ ...period, end: value })}
                slotProps={{ textField: { size: "small", sx: { width: 140 } } }}
              />
            </Box>
          </LocalizationProvider>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              size="small"
              onClick={() => setPeriod({ start: null, end: null })}
              disabled={!hasPeriod}
            >
              {t("MSG_MeetingAgendaClearPeriod")}
            </Button>
            <Button size="small" variant="contained" sx={{ ml: "auto" }} onClick={() => setAnchorEl(null)}>
              {t("close")}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default AgendaPeriodFilter;
